import { useEffect, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { stepFadeIn } from './motion';
import { useWizardModalName } from './wizardStepContext';

/**
 * ModalShell — port from ~/FrontendFinal/Design/src/components/ModalShell.tsx.
 *
 * The FADING CONTENTS of the upload wizard (header/body/footer slot wrappers,
 * wrapped in a `motion.div` carrying `stepFadeIn`). The bordered box, its
 * `role="dialog"`, `aria-modal`, `tabIndex` and the focus-trap are the PERSISTENT
 * frame — in the Design that frame is UploadWizard, and in the backend it is now
 * the COPY of that same file (`../pages/UploadWizard`) for the upload steps, with
 * the earlier hand-written `OverlayFrame` still serving FlowPickerReskin. This
 * component is what either frame's <AnimatePresence> swaps per step: only the
 * contents cross-fade in place, the box never moves.
 *
 * The geometry (the header inset arithmetic, the body's overflow, the footer's
 * shrink-0) is byte-identical to the Design; see the Design repo's header for the
 * full derivation. TWO import repoints from the Design source, nothing else:
 *   `../styles/motion`      → `./motion`             (ported alongside)
 *   `../pages/UploadWizard` → `./wizardStepContext`  (a shared module, because the
 *      backend has TWO frames that provide this context and one shared
 *      createContext is what lets this component work unchanged under either —
 *      see that module's header)
 *
 * ACCESSIBLE NAME. The `role="dialog"` element is the persistent frame, but the
 * name is per-step (P1 points at a visible title via `labelledBy`; P2–P5 have no
 * title and use `label`). So this reports its name UP to the frame via
 * `useWizardModalName()` in an effect; the frame renders the resulting
 * `aria-labelledby`/`aria-label`.
 */

export interface ModalShellProps {
  /** Accessible name: id of the visible title where the frame draws one (P1). */
  labelledBy?: string;
  /** Fallback name for frames that draw no title (P2–P5 lead with step pills). */
  label?: string;
  /** Top region: the title on P1, the step pills on P2–P5. */
  header?: ReactNode;
  /**
   * Inset for the header region — BOTH axes. P2–P5 pills: 33 top (space-11) and
   * 65 both sides (closes the modal width exactly). P1 title: 40 top (space-12)
   * and the shared --modal-pad-x sides.
   */
  headerInset?: 'pills' | 'title';
  /** Scrolls if the content exceeds the fixed 640 height. */
  children?: ReactNode;
  /**
   * Whether the BODY is the scroll container (default true — the design's own
   * behaviour, and what P1's table needs). Pass false on the two-column wizard
   * steps, where the left rail and footer must stay put and the page's own form
   * column is the scroller. See the note at the body div.
   */
  bodyScroll?: boolean;
  /** Footer region. P1 passes its own rule; P2–P5 do not. */
  footer?: ReactNode;
  /**
   * Dismissal. The design draws no close affordance; Escape-to-close and click-off
   * are wired on the frame (OverlayFrame), not here.
   */
  onDismiss?: () => void;
}

export function ModalShell({
  labelledBy,
  label,
  header,
  headerInset = 'pills',
  children,
  footer,
  bodyScroll = true,
}: ModalShellProps) {
  // Report the per-step accessible name UP to the persistent frame. An effect (not
  // render) is safe because the name is not visually rendered, and with mode="wait"
  // only one step is ever mounted, so there is never a race between two names.
  const setModalName = useWizardModalName();
  useEffect(() => {
    // Build the name object conditionally: under exactOptionalPropertyTypes an
    // explicit `undefined` is not assignable to an optional `string`, so we omit
    // the key rather than set it to undefined.
    setModalName({
      ...(labelledBy !== undefined ? { labelledBy } : {}),
      ...(label !== undefined ? { label } : {}),
    });
  }, [setModalName, labelledBy, label]);

  return (
    <motion.div
      // Step-to-step motion is `stepFadeIn` — a PURE opacity cross-dissolve, no
      // travel: the frame does not move between steps. The frame's
      // <AnimatePresence mode="wait"> plays one step's fade-out before the next
      // fades in. Opacity-only is reduced-motion-safe by construction.
      variants={stepFadeIn}
      initial="hidden"
      animate="visible"
      exit="exit"
      // Fills the persistent frame so the body's overflow resolves against the
      // frame's fixed height.
      //
      // `bg-surface` and `rounded-lg` are HERE FOR CHROME, and are not a second
      // copy of the frame's styling for its own sake. Animating opacity promotes
      // this element to its own compositing layer; a promoted layer with no
      // background of its own gets composited against the page ground rather than
      // against its parent's --surface, and its bounds are square while the frame
      // it sits in is rounded. In Chrome that showed as a white flash across the
      // step change (P1 -> P2 most visibly, the widest content change). Firefox
      // and Safari rasterise the subtree differently and never showed it.
      //
      // Painting the same --surface on the fading layer itself means there is no
      // moment where anything but --surface is behind the content, and matching
      // `rounded-lg` keeps the square layer from squaring off the frame's corners
      // mid-fade. Both are the frame's own values (see UploadWizard's persistent
      // frame) — visually a no-op at rest, load-bearing only during the fade.
      className="flex min-h-0 flex-1 flex-col rounded-lg bg-surface"
    >
      {header ? (
        <div
          className={[
            'shrink-0',
            headerInset === 'title' ? 'px-modal-x pt-12' : 'px-step-row pt-11',
          ].join(' ')}
        >
          {header}
        </div>
      ) : null}

      {/* The body scrolls, not the modal. min-h-0 is what makes the overflow
          resolve inside a flex column rather than growing the parent.

          `bodyScroll={false}` moves that scroller INWARD one level, for the
          two-column wizard steps (P2/P3). There, the left identity/preview rail
          and the footer are STATIONARY and only the form column scrolls — so the
          page owns the scroller and this body must not be one, or the rail and
          footer ride up with the fields. The design's own P2/P3 never hit this
          because they draw a fixed field count that always fits; the backend's
          field list is dynamic, so the distinction becomes visible. */}
      <div
        className={[
          'min-h-0 flex-1 px-modal-x',
          // `flex flex-col` in the non-scrolling case is load-bearing, not tidiness.
          // This body is a flex ITEM with no explicit height, so a child's `h-full`
          // (height: 100%) resolves against an INDEFINITE height — i.e. to `auto`.
          // The two-column grid then grows past the frame's 639px instead of being
          // capped by it, the form column gets no bounded height, its flex-1 has no
          // slack to divide, `overflow-y-auto` never activates and the footer is
          // pushed below the visible box. Making this a flex column instead gives
          // the child a definite flex basis to resolve against.
          bodyScroll ? 'overflow-y-auto' : 'flex flex-col overflow-hidden',
        ].join(' ')}
      >
        {children}
      </div>

      {footer ? <div className="shrink-0">{footer}</div> : null}
    </motion.div>
  );
}
